import { useCallback, useState } from 'react';
import { formatTranslation, unsafeTranslationKey } from 'intl';
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useUsers, withUsers } from 'components/dataProviders/withUsers';
import { usersWithAccessSelector } from 'components/dataProviders/withUsers/model';
import { MemoListSearch } from 'components/issue/ListSearch/ListSearch';
import { useIntl } from 'react-intl';
import { useProjectOrganizationId } from 'store/project';
import { useCurrentUserInProjectModel } from 'store/user';
import { AccessLevelModel } from 'shared/domain/issue/mapping/accessLevel/model/types';
import { getAccessLevelOnDropdownView } from 'shared/domain/issue/mapping/accessLevel/toOnView/getAccessLevelOnDropdownView';
import { UserRole } from 'shared/types/userRole';
import {
  genericSearchPhraseValidation,
  normalizeForSearch,
} from 'shared/utils/search';
import {
  calculateUsersToDisplay,
  rowSort,
} from '../../../../shared/domain/issue/access/helpers';
import { useStyles } from './style';
import { accessLevelColumn, AccessRow } from './table/accessRow';
import { nameColumn, NameRow } from './table/nameRow';
import { roleColumn, RoleRow } from './table/roleRow';
import { StyledTableHeaderCell } from './table/tableHeaderCell';
import { AccessManagementProps } from './types';
import { usersToRows } from './usersToRows';
import { DialogTranslationKeys } from 'intl/keys';

const columns = [nameColumn, roleColumn, accessLevelColumn];

function _AccessManagement({
  site,
  process,
  userAccesses,
  onChange,
}: AccessManagementProps): JSX.Element | null {
  const intl = useIntl();
  const classes = useStyles();
  const [phrase, setPhrase] = useState('');
  const {
    all: { items: allUsers },
    refreshUsers,
  } = useUsers();
  const submitter = useCurrentUserInProjectModel();
  const organizationId = useProjectOrganizationId();

  const handlePhraseChange = useCallback((phrase: string) => {
    setPhrase(normalizeForSearch(phrase));
  }, []);
  const onSelectChange = useCallback(
    (
      newAccessLevel: AccessLevelModel,
      currentAccessLevel: AccessLevelModel,
      userId: string,
      userLabel: string
    ): void => {
      onChange(
        newAccessLevel,
        currentAccessLevel,
        userId,
        userLabel,
        userAccesses
      );
    },
    [onChange, userAccesses]
  );

  const usersWithAccess = usersWithAccessSelector(
    [
      UserRole.manager,
      UserRole.viewer,
      UserRole.standard,
      UserRole.projectAdmin,
      UserRole.organizationAdmin,
    ],
    site,
    process,
    allUsers,
    organizationId
  );

  const displayedUsers = calculateUsersToDisplay(
    userAccesses,
    usersWithAccess,
    phrase
  );
  const displayedRows = usersToRows(
    Array.from(displayedUsers.values()),
    userAccesses,
    submitter,
    site,
    process
  ).sort(rowSort);

  return (
    <div className={classes.wrapper}>
      <div className={classes.search}>
        <MemoListSearch
          onSearch={handlePhraseChange}
          data-qa='access-management-search'
          placeholder={formatTranslation(intl, DialogTranslationKeys.placeholderSearch,)}
          phraseValidation={genericSearchPhraseValidation}
        />
      </div>
      <div className='table'>
        <TableContainer>
          <Table stickyHeader aria-label='sticky table'>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <StyledTableHeaderCell
                    key={column.id}
                    align={column.align}
                    style={column.style}
                  >
                    {formatTranslation(intl, unsafeTranslationKey(column.label) )}
                  </StyledTableHeaderCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedRows.map((row) => {
                const user = displayedUsers.get(row._id)!;
                const accessLevelOnDropdownView =
                  getAccessLevelOnDropdownView({
                    issueUserAccesses: userAccesses,
                    submitter,
                    targetUser: user,
                    siteId: site,
                    processId: process,
                  });
                return (
                  <TableRow
                    hover
                    role='checkbox'
                    tabIndex={-1}
                    key={row._id}
                  >
                    <NameRow label={user.label} />
                    <RoleRow role={user.role} />
                    <AccessRow
                      user={user}
                      accessLevelOnDropdownView={accessLevelOnDropdownView}
                      onAccessChange={onSelectChange}
                    />
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </div>
  );
}

export const AccessManagement = withUsers(_AccessManagement);
